/**
 * "What's ahead" panel.
 *
 * Everything the app already knows about the journey, gathered in one place a
 * driver can check at a glance while stopped: how much is left, where the time
 * actually goes, what the terrain does, what the roads are, and what will make
 * them stop. None of it is new computation - it is the same route stats,
 * gradient analysis and pace model used elsewhere, presented rather than
 * re-derived.
 *
 * Works both before setting off (whole route) and mid-drive (remaining only),
 * because "the route ahead" means different things in those two states.
 */

import type { NavProgress, Route } from '../core/types';
import type { RouteStats } from '../nav/routeStats';
import type { ElevationProfile } from '../nav/elevation';
import type { FuelStop } from '../nav/fuelStops';
import type { PaceSpeeds, RoadTier } from '../nav/paceModel';
import { tierForStep } from '../nav/paceModel';
import { paceDuration } from '../nav/paceModel';
import type { UnitSystem } from '../core/settings';
import { formatDistance, formatDuration, formatEta } from '../core/format';
import { t } from '../core/i18n';

export interface RouteInfoState {
  route: Route | null;
  stats: RouteStats | null;
  elevation: ElevationProfile | null;
  /** Live progress while navigating; null before departure. */
  progress: NavProgress | null;
  fuelStop: FuelStop | null;
  units: UnitSystem;
  speeds: PaceSpeeds;
  vehicleName: string;
}

export class RouteInfoSheet {
  readonly button: HTMLElement;
  private sheet: HTMLElement;
  private get: () => RouteInfoState;

  constructor(parent: HTMLElement, getState: () => RouteInfoState) {
    this.get = getState;

    this.button = document.createElement('button');
    this.button.className = 'info-fab';
    this.button.hidden = true;
    this.button.setAttribute('aria-label', t('info.title'));
    this.button.innerHTML = `
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" stroke-width="1.9"/>
        <path d="M12 10.5 V17" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
        <circle cx="12" cy="7.2" r="1.3" fill="currentColor"/>
      </svg>`;
    parent.appendChild(this.button);
    this.button.addEventListener('click', () => this.open());

    this.sheet = document.createElement('div');
    this.sheet.className = 'info-sheet';
    this.sheet.hidden = true;
    parent.appendChild(this.sheet);
    const touched = () => (this.touchedAt = Date.now());
    this.sheet.addEventListener('touchstart', touched, { passive: true });
    this.sheet.addEventListener('touchmove', touched, { passive: true });
    this.sheet.addEventListener('scroll', touched, { passive: true, capture: true });
  }

  setVisible(visible: boolean): void {
    this.button.hidden = !visible;
    if (!visible) this.close();
  }

  close(): void {
    this.sheet.hidden = true;
  }

  open(): void {
    this.sheet.hidden = false;
    this.render(true);
  }

  /** Redraw only if already open, so live progress stays current. */
  refresh(): void {
    if (!this.sheet.hidden) this.render();
  }

  /**
   * Re-rendering wholesale resets the scroll position, and this runs on every
   * GPS fix - so reading anything below the fold was impossible: the sheet
   * snapped back to the top about once a second. Reported from the road as
   * "the route ahead scrolls to top automatically all the time."
   *
   * Two guards: keep the scroll offset across the swap, and hold off entirely
   * while a finger is on the panel, since replacing the DOM mid-flick kills
   * momentum scrolling even when the offset is restored.
   */
  private touchedAt = 0;

  private render(force = false): void {
    const scroller = this.sheet.querySelector<HTMLElement>('.info-sheet__scroll');
    if (!force && scroller && Date.now() - this.touchedAt < 2000) return;
    const keepTop = scroller?.scrollTop ?? 0;
    this.renderInner();
    const next = this.sheet.querySelector<HTMLElement>('.info-sheet__scroll');
    if (next && keepTop) next.scrollTop = keepTop;
  }

  private renderInner(): void {
    const s = this.get();
    if (!s.route) {
      this.close();
      return;
    }

    const sections = [
      this.aheadSection(s),
      this.timeSection(s),
      this.terrainSection(s),
      this.roadsSection(s),
      this.stopsSection(s),
    ]
      .filter(Boolean)
      .join('');

    this.sheet.innerHTML = `
      <div class="info-sheet__card">
        <div class="info-sheet__head">
          <h3>${t('info.title')}</h3>
          <button class="icon-btn" data-act="close" aria-label="${t('preview.cancel')}">
            <svg viewBox="0 0 24 24"><path d="M6 6 L18 18 M18 6 L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>
          </button>
        </div>
        <div class="info-sheet__scroll">${sections}</div>
      </div>`;

    this.sheet.querySelector('[data-act=close]')!.addEventListener('click', () => this.close());
    this.sheet.addEventListener('click', (e) => {
      if (e.target === this.sheet) this.close();
    });
  }

  /* ------------------------------ sections ------------------------------ */

  private aheadSection(s: RouteInfoState): string {
    const route = s.route!;
    const remaining = s.progress ? s.progress.distanceRemaining : route.distance;
    const seconds = s.progress ? s.progress.durationRemaining : paceDuration(route);

    return section(t('info.ahead'), [
      row(t('info.remaining'), formatDistance(remaining, s.units)),
      row(t('info.timeLeft'), formatDuration(seconds)),
      row(t('info.arrival'), formatEta(seconds), true),
    ]);
  }

  private timeSection(s: RouteInfoState): string {
    const route = s.route!;
    const atLights = route._paceSignalSeconds ?? 0;
    const total = paceDuration(route);
    if (total <= 0) return '';
    const rolling = Math.max(0, total - atLights);

    // Where the journey time actually goes - the split the ETA hides.
    const pct = Math.round((atLights / total) * 100);
    return section(t('info.time'), [
      row(t('info.driving'), formatDuration(rolling)),
      atLights > 60 ? row(t('info.atLights'), `${formatDuration(atLights)} (${pct}%)`, true) : '',
      row(t('info.routerSaid'), formatDuration(route.duration)),
    ]);
  }

  private terrainSection(s: RouteInfoState): string {
    const p = s.elevation;
    if (!p) return '';

    const rows = [
      row(t('info.climb'), `${p.totalAscent} m`),
      row(t('info.descent'), `${p.totalDescent} m`),
      row(t('info.highest'), `${p.maxEle} m`),
    ];
    if (p.steepestClimb > 0) {
      rows.push(row(t('info.steepestUp'), `${p.steepestClimb}%`, p.steepestClimb >= 8));
    }
    if (p.steepestDescent < 0) {
      rows.push(
        row(t('info.steepestDown'), `${Math.abs(p.steepestDescent)}%`, p.steepestDescent <= -8),
      );
    }

    const worst = p.notable[0];
    const note = p.blocking
      ? `<div class="info-note info-note--bad">${t('info.tooSteep', { name: escapeHtml(s.vehicleName) })}</div>`
      : worst && Math.abs(worst.grade) >= 6
        ? `<div class="info-note">${t('info.brakeCare', {
            pct: String(Math.abs(worst.grade)),
            km: ((worst.to - worst.from) / 1000).toFixed(1),
          })}</div>`
        : '';

    return section(t('info.terrain'), rows) + note;
  }

  private roadsSection(s: RouteInfoState): string {
    const route = s.route!;
    const byTier: Record<RoadTier, number> = { motorway: 0, dual: 0, single: 0 };
    for (const leg of route.legs) {
      for (const step of leg.steps) byTier[tierForStep(step)] += step.distance;
    }
    const total = byTier.motorway + byTier.dual + byTier.single;
    if (total <= 0) return '';

    const bar = (['motorway', 'dual', 'single'] as RoadTier[])
      .map(
        (k) =>
          `<span class="info-bar__seg info-bar__seg--${k}" style="flex:${Math.max(byTier[k], 1)}"></span>`,
      )
      .join('');

    return (
      section(t('info.roads'), [
        row(`${t('set.pace.motorway')} · ${s.speeds.motorway} km/h`, formatDistance(byTier.motorway, s.units)),
        row(`${t('set.pace.dual')} · ${s.speeds.dual} km/h`, formatDistance(byTier.dual, s.units)),
        row(`${t('set.pace.single')} · ${s.speeds.single} km/h`, formatDistance(byTier.single, s.units)),
      ]) + `<div class="info-bar">${bar}</div>`
    );
  }

  private stopsSection(s: RouteInfoState): string {
    const st = s.stats;
    const rows: string[] = [];
    if (st) {
      rows.push(row(t('info.signals'), String(st.trafficSignals)));
      if (st.tollBooths > 0) rows.push(row(t('info.tolls'), String(st.tollBooths)));
      if (st.railwayCrossings > 0) rows.push(row(t('info.crossings'), String(st.railwayCrossings)));
      rows.push(row(t('info.turns'), String(st.turns)));
    }
    if (s.fuelStop && s.progress) {
      const away = Math.max(0, s.fuelStop.along - s.progress.snapped.distanceAlongRoute);
      rows.push(row(t('info.nextFuel'), formatDistance(away, s.units), true));
    }
    if (!rows.length) return '';
    return section(t('info.stops'), rows);
  }
}

/* -------------------------------- helpers -------------------------------- */

function section(title: string, rows: string[]): string {
  const body = rows.filter(Boolean).join('');
  if (!body) return '';
  return `<section class="info-group"><h4>${title}</h4>${body}</section>`;
}

function row(label: string, value: string, emphasise = false): string {
  return `<div class="info-row ${emphasise ? 'is-key' : ''}">
    <span>${label}</span><strong>${value}</strong></div>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}
